import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { StarRating } from '../components/StarRating';
import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getProducts, getReviews, resolveMediaUrl } from '../lib/api';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { resolveHomeContent } from '../lib/clinicSiteDefaults';
import AddressMapLink from '../components/AddressMapLink';
import WhatsAppLink from '../components/WhatsAppLink';

function formatProductPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

function iconFromApiName(name) {
  const pascal = String(name || 'shield')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
  return Icons[pascal] || Icons.Shield;
}

export default function HomePage() {
  const { info, loading: brandingLoading } = useClinicBranding();
  const brandingWait = brandingLoading && !info;
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setProductsLoading(true);
      try {
        const productsData = await getProducts();
        if (alive) {
          setProducts(Array.isArray(productsData) ? productsData.slice(0, 3) : []);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        if (alive) setProducts([]);
      } finally {
        if (alive) setProductsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const reviewsData = await getReviews();
        if (alive) {
          setReviews(Array.isArray(reviewsData) ? reviewsData.slice(0, 3) : []);
        }
      } catch (error) {
        console.error('Error loading reviews:', error);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const home = useMemo(() => resolveHomeContent(info), [info]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-sage-50 to-background" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fadeIn">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                {home.heroBadge}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight">
                {home.heroPrefix}{' '}
                <span className="text-primary">{home.heroHighlight}</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">{home.heroSubtitle}</p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/agendar">
                  <Button size="lg" className="rounded-full text-base px-8 gap-2" data-testid="hero-book-btn">
                    Agendar Cita
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/productos">
                  <Button variant="outline" size="lg" className="rounded-full text-base px-8" data-testid="hero-services-btn">
                    Ver Servicios
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-3xl transform rotate-3" />
              {brandingWait ? (
                <div
                  className="relative rounded-3xl w-full h-[400px] bg-muted animate-pulse"
                  aria-hidden
                />
              ) : (
                <img
                  key={home.heroImg}
                  src={resolveMediaUrl(home.heroImg) || home.heroImg}
                  alt={home.brand}
                  className="relative rounded-3xl shadow-ambient-lg w-full h-[400px] object-cover"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {home.features.map((feature, index) => {
              const Cmp = iconFromApiName(feature.icon);
              return (
                <Card key={index} className="border border-border p-6 text-center card-hover">
                  <CardContent className="p-0 space-y-3">
                    <div className="w-12 h-12 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                      <Cmp className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section id="servicios" className="py-16 bg-sage-50/50 scroll-mt-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">{home.servicesTitle}</h2>
              <p className="text-muted-foreground mb-8">{home.servicesIntro}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {home.services.map((service, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-border">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                    <span className="text-foreground">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/productos">
                  <Button variant="outline" className="rounded-full gap-2">
                    Ver todos los servicios
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              {brandingWait ? (
                <div className="rounded-3xl w-full h-[400px] bg-muted animate-pulse" aria-hidden />
              ) : (
                <img
                  key={home.clinicImg}
                  src={resolveMediaUrl(home.clinicImg) || home.clinicImg}
                  alt={home.brand}
                  className="rounded-3xl shadow-ambient w-full h-[400px] object-cover"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="productos" className="py-16 bg-white scroll-mt-28" data-testid="products-preview">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{home.prodTitle}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{home.prodIntro}</p>
          </div>
          {productsLoading ? (
            <p className="text-center text-muted-foreground animate-pulse">Cargando productos…</p>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="border border-border overflow-hidden card-hover" data-testid={`product-card-${product.id}`}>
                  {product.image_url && (
                    <img
                      src={resolveMediaUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  {!product.image_url && (
                    <div className="w-full h-48 bg-sage-100 flex items-center justify-center">
                      <span className="text-sage-400 text-sm">Sin imagen</span>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-lg mb-2">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-primary">${formatProductPrice(product.price)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">No hay productos disponibles en este momento.</p>
          )}
          <div className="text-center mt-8">
            <Link to="/productos">
              <Button className="rounded-full gap-2" data-testid="home-view-all-products-btn">
                Ver todos los productos
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="py-16 bg-sage-50/50" data-testid="reviews-preview">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{home.reviewsTitle}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <Card key={review.id} className="border border-border p-6" data-testid={`review-card-${review.id}`}>
                  <CardContent className="p-0 space-y-4">
                    <StarRating rating={review.rating} />
                    <p className="text-foreground italic">&quot;{review.comment}&quot;</p>
                    <p className="text-sm text-muted-foreground font-medium">— {review.patient_name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/resenas">
                <Button variant="outline" className="rounded-full gap-2">
                  Ver todas las reseñas
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">{home.ctaTitle}</h2>
          <p className="text-primary-foreground/80 text-lg mb-8">{home.ctaSub}</p>
          <Link to="/agendar">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full text-base px-8 gap-2 bg-white text-primary hover:bg-white/90"
              data-testid="cta-book-btn"
            >
              Agendar Cita Ahora
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{home.contactTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border border-border p-8">
              <CardContent className="p-0 space-y-4">
                <h3 className="font-heading text-xl font-semibold">Información de Contacto</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Dirección:</strong>{' '}
                    <AddressMapLink
                      address={home.addr}
                      className="text-primary hover:underline underline-offset-2"
                    />
                  </p>
                  <p>
                    <strong className="text-foreground">Teléfono:</strong>{' '}
                    <WhatsAppLink
                      phone={home.phone}
                      className="text-primary hover:underline underline-offset-2"
                    />
                  </p>
                  <p>
                    <strong className="text-foreground">Email:</strong> {home.email}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-border p-8">
              <CardContent className="p-0 space-y-4">
                <h3 className="font-heading text-xl font-semibold">Horarios de Atención</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>{home.footerHoursWeekdays}</p>
                  <p>{home.footerHoursLunch}</p>
                  <p>{home.footerHoursWeekend}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
